import React, { useRef } from "react";
import { Box } from "@material-ui/core";

import { AddCommas, convertValuesFM } from "../../../shared-resources/utils";
import { PaginationData } from "../../../common-components/PaginationData";
import { Details } from "@material-ui/icons";
import DeleteForeverIcon from "@material-ui/icons/DeleteForeverOutlined";
import ColumnPicker from "./ColumnPicker";

const IDENTITY_COLUMNS = [
  { key: "projectId", label: "Project Id", width: 120 },
  { key: "projectName", label: "Project Name", width: 180 },
  { key: "account", label: "Account", width: 120 },
  { key: "accountName", label: "Account Name", width: 205 },
  { key: "details", label: "Details", width: 55 },
  { key: "departmentName", label: "Department", width: 150 },
  { key: "costType", label: "Cost Type", width: 130 },
];

// Same approach as RenderLabor.js: the table body scrolls inside a capped-height box while its
// own `<th>`s stay pinned via CSS position: sticky, matching Fringe.js's visible outcome
// (headers never scroll out of view) without splitting into separate header/body tables.
// `top: "40px"` on the second header row matches the first row's own fixed 40px height so the
// two rows stack without overlapping.
//
// A FIXED pixel height (same value as RenderLabor.js, same row-height CSS), applied
// unconditionally: 10 or fewer rows render shorter than this with no scrollbar, while more
// rows scroll WITHIN this same height instead of the box shrinking or resizing with page size.
const BODY_SCROLL_STYLE = { maxHeight: "560px", overflowY: "auto" };
const STICKY_TH_ROW1 = { position: "sticky", top: 0, zIndex: 2 };
const STICKY_TH_ROW2 = { position: "sticky", top: "40px", zIndex: 2 };
// Both tables' headers are 80px tall (identity table: one 80px CSS-default row; months table:
// two explicit 40px rows) — pins the Total row directly below whichever header sits above it,
// so it's visible without scrolling, same as Labor's Headcount/Total rows.
const STICKY_TOTALS_ROW = { position: "sticky", top: "80px", zIndex: 1, background: "#fff" };
// The "+ Add New" row is pinned to the BOTTOM of the scrollable area the same way the Total
// row is pinned to the top — always reachable without scrolling all the way down.
const STICKY_BOTTOM_ROW = { position: "sticky", bottom: 0, zIndex: 1, background: "#fff" };

const RenderLaborODC = ({
  classes,
  laborODCData,
  months,
  handleInputChange,
  isEdit,
  handleLaborODCMonthlySalaryChange,
  calculateTotalAnnualTarget,
  calculateMonthlyLaborODCTotal,
  calculateMonthlyTarget,
  editedLaborODCData,
  handleScrollTable,
  LaborODCRef,
  filteredlaborODCData,
  setFilteredLaborODCData,
  calculateODCTotalByProject,
  handleLaborOdcTotalChange,
  onClickNonLaborDetails,
  versionStatus,
  handleAddNewNonLabor,
  onDeleteNonLaborClick,
  isEditable,
  isEditableBudget,
  departmentName,
  hiddenColumns,
  onToggleColumn,
  onResetColumns,
}) => {
  const columns = IDENTITY_COLUMNS.filter(
    (col) => !hiddenColumns.includes(col.key),
  );

  const renderCell = (col, item) => {
    switch (col.key) {
      case "projectId":
        return item.projectId;
      case "projectName":
        return item.projectName;
      case "account":
        return item.account;
      case "accountName":
        return item.accountName;
      case "details":
        return (
          (Array.isArray(item.listOfNotes) ||
            (item.listOfNotes !== null && item.listOfNotes !== undefined)) && (
            <span
              style={{
                fontWeight: 600,
                color: "#09659c",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => onClickNonLaborDetails(item)}
            >
              View
            </span>
          )
        );
      case "departmentName":
        return item.department || departmentName;
      case "costType":
        return item.costType;
      default:
        return null;
    }
  };

  const cellTitle = (col, item) => {
    if (col.key === "details") {
      return Array.isArray(item.listOfNotes)
        ? item.listOfNotes.filter((n) => n && n.trim()).join(", ")
        : item.listOfNotes;
    }
    if (col.key === "departmentName") return item.department || departmentName;
    return item[col.key];
  };

  // One visible vertical scrollbar, not two — same pairing as RenderLabor.js/Fringe.js: both
  // panes stay independently scrollable, but their scroll position is kept in sync here, and
  // the left pane's own scrollbar is hidden via classes.hiddenScrollbar.
  //
  // Synced by RATIO (fraction of max scroll), not raw scrollTop pixels — the two tables are
  // laid out independently and can end up with slightly different scrollHeights even with the
  // same row count, so copying scrollTop directly drifts the closer you get to the bottom
  // (each pane clamps to its own max). Ratio sync guarantees both reach 0%/100% together.
  const leftPaneRef = useRef(null);
  const syncScrollByRatio = (source, target) => {
    if (!source || !target) return;
    const sourceMax = source.scrollHeight - source.clientHeight;
    const targetMax = target.scrollHeight - target.clientHeight;
    if (sourceMax <= 0 || targetMax <= 0) return;
    target.scrollTop = (source.scrollTop / sourceMax) * targetMax;
  };
  const syncVerticalFromLeft = () =>
    syncScrollByRatio(leftPaneRef.current, LaborODCRef.current);
  const syncVerticalFromRight = () =>
    syncScrollByRatio(LaborODCRef.current, leftPaneRef.current);

  return (
  <>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "16px",
      }}
    >
      <div className={classes.tableTitle}>Non Labor Cost</div>
      <ColumnPicker
        columns={IDENTITY_COLUMNS}
        hidden={hiddenColumns}
        onToggle={onToggleColumn}
        onReset={onResetColumns}
      />
    </div>
    {laborODCData && laborODCData.length > 0 ? (
      <div>
        <Box>
          <Box display="flex" flexDirection="row">
            <Box
              ref={leftPaneRef}
              className={classes.hiddenScrollbar}
              onScroll={syncVerticalFromLeft}
              style={{
                boxShadow: "0px 3px 6px #00000029",
                maxWidth: "720px",
                minWidth: "720px",
                overflowX: "auto",
                marginRight: "8px",
                ...BODY_SCROLL_STYLE,
              }}
            >
              <table
                className="firstTableFin"
                cellPadding={0}
                cellSpacing={0}
                style={{
                  width: "1000px",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: "24px",
                        ...STICKY_TH_ROW1,
                      }}
                    ></th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        style={{
                          minWidth: `${col.width}px`,
                          maxWidth: `${col.width}px`,
                          ...STICKY_TH_ROW1,
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="totalRow">
                    <td
                      colSpan={columns.length + 1}
                      style={{ fontWeight: 600, ...STICKY_TOTALS_ROW }}
                    >
                      Total
                    </td>
                  </tr>
                  {filteredlaborODCData.map((item) => {
                    const originalIndex = item.originalIndex;
                    return (
                      <tr key={originalIndex}>
                        <td
                          style={{
                            minWidth: "24px",
                            maxWidth: "24px",
                          }}
                        >
                          {isEditable && (
                            <DeleteForeverIcon
                              style={{
                                color: "red",
                                fontSize: "16px",
                                cursor: "pointer",
                                float: "left",
                              }}
                              onClick={() => onDeleteNonLaborClick(item)}
                            />
                          )}
                        </td>
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            style={{
                              minWidth: `${col.width}px`,
                              maxWidth: `${col.width}px`,
                            }}
                            title={cellTitle(col, item)}
                          >
                            {renderCell(col, item)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}

                  <tr>
                    <td colSpan={2} style={STICKY_BOTTOM_ROW}>
                      {!isEdit &&
                        versionStatus &&
                        isEditable &&
                        ((versionStatus.toLowerCase() === "open" &&
                          localStorage.getItem("accessType") === "RW") ||
                          isEditableBudget) && (
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#09659c",
                              fontWeight: 600,
                            }}
                            onClick={() => handleAddNewNonLabor()}
                          >
                            + Add New
                          </span>
                        )}
                    </td>
                    <td
                      colSpan={Math.max(columns.length - 1, 1)}
                      style={STICKY_BOTTOM_ROW}
                    ></td>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Box
              ref={LaborODCRef}
              style={{
                overflowX: "auto",
                boxShadow: "0px 3px 6px #00000029",
                ...BODY_SCROLL_STYLE,
              }}
              onScroll={() => {
                handleScrollTable("LaborODC");
                syncVerticalFromRight();
              }}
            >
              <Box display="flex" flexDirection="row">
                <Box
                  style={{
                    boxShadow: "0px 3px 6px #00000029",
                    marginRight: "8px",
                  }}
                >
                  <table
                    className="firstTableFin"
                    cellPadding={0}
                    cellSpacing={0}
                  >
                    <thead>
                      <tr>
                        {months.map((month) => (
                          <th
                            key={month.heading}
                            className="centerAlignedHeaderLeftBorder"
                            style={{
                              minWidth: "90px",
                              maxWidth: "90px",
                              height: "40px",
                              ...STICKY_TH_ROW1,
                            }}
                          >
                            {month.heading}
                          </th>
                        ))}
                        <th
                          rowSpan={2}
                          className="centerAlignedHeaderLeftBorder"
                          style={{
                            minWidth: "90px",
                            maxWidth: "90px",
                            ...STICKY_TH_ROW1,
                          }}
                        >
                          Total
                        </th>
                      </tr>
                      <tr>
                        {months.map((month, i) => (
                          <th
                            key={i}
                            className={
                              month.type === "Actual"
                                ? "actualsHeaderCell"
                                : "forecastHeaderCell"
                            }
                            style={{ height: "40px", ...STICKY_TH_ROW2 }}
                          >
                            {month.type === "Actual" ? "Actuals" : "Forecast"}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="totalRow">
                        {months.map((month) => (
                          <td
                            key={month.heading}
                            className={classes.centerAlignedData}
                            style={STICKY_TOTALS_ROW}
                          >
                            <strong>
                              {convertValuesFM(
                                calculateMonthlyLaborODCTotal(month),
                              )}
                            </strong>
                          </td>
                        ))}
                        <td
                          className={classes.centerAlignedData}
                          style={STICKY_TOTALS_ROW}
                        >
                          <strong>
                            {convertValuesFM(calculateTotalAnnualTarget())}
                          </strong>
                        </td>
                      </tr>
                      {filteredlaborODCData.map((odc) => {
                        const originalIndex = odc.originalIndex;

                        return (
                          <tr key={`${odc.account}-${originalIndex}`}>
                            {months.map((month) => (
                              <td
                                key={`${month}-${originalIndex}`}
                                className={classes.centerAlignedData}
                              >
                                {isEdit &&
                                month.type.toLowerCase() === "forecast" ? (
                                  <input
                                    type="text"
                                    className={classes.inputStyle}
                                    value={AddCommas(
                                      editedLaborODCData[originalIndex]?.[
                                        month.heading
                                      ],
                                    )}
                                    onChange={(e) => {
                                      const input = e.target;
                                      const cursorPos = input.selectionStart;
                                      const oldValue = input.value;
                                      const rawValue = oldValue.replace(
                                        /,/g,
                                        "",
                                      );
                                      const commasBefore = (
                                        oldValue
                                          .slice(0, cursorPos)
                                          .match(/,/g) || []
                                      ).length;
                                      const newFormatted = String(
                                        AddCommas(
                                          rawValue === "" ? 0 : rawValue,
                                        ),
                                      );
                                      const commasAfter = (
                                        newFormatted
                                          .slice(0, cursorPos)
                                          .match(/,/g) || []
                                      ).length;
                                      const newCursorPos =
                                        cursorPos +
                                        (commasAfter - commasBefore);
                                      handleLaborODCMonthlySalaryChange(
                                        originalIndex,
                                        month.heading,
                                        rawValue,
                                      );
                                      requestAnimationFrame(() => {
                                        input.setSelectionRange(
                                          newCursorPos,
                                          newCursorPos,
                                        );
                                      });
                                    }}
                                  />
                                ) : (
                                  convertValuesFM(
                                    calculateMonthlyTarget(odc, month),
                                  )
                                )}
                              </td>
                            ))}
                            <td className={classes.centerAlignedData}>
                              {convertValuesFM(
                                calculateODCTotalByProject(originalIndex),
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Empty placeholder, not a totals row — Total moved to the top of this
                          table. Kept as a sticky-bottom row purely so this table's row
                          count/height still matches the identity table's own sticky-bottom
                          "+ Add New" row, keeping the two panes visually aligned while scrolled
                          to the bottom. */}
                      <tr>
                        {months.map((month) => (
                          <td
                            key={month}
                            className={classes.centerAlignedData}
                            style={STICKY_BOTTOM_ROW}
                          />
                        ))}
                        <td
                          className={classes.centerAlignedData}
                          style={STICKY_BOTTOM_ROW}
                        />
                      </tr>
                    </tbody>
                  </table>
                </Box>
              </Box>
            </Box>
          </Box>
          <PaginationData
            data={laborODCData}
            setFilteredData={setFilteredLaborODCData}
            title="NonLaborCost"
          />
        </Box>
      </div>
    ) : (
      <div
        style={{
          textAlign: "center",
          fontWeight: 600,
          background: "#fff",
          padding: "24px 0",
        }}
      >
        {!isEdit &&
        isEditable &&
        ((versionStatus.toLowerCase() === "open" &&
          localStorage.getItem("accessType") === "RW") ||
          isEditableBudget) ? (
          <span
            style={{
              cursor: "pointer",
              color: "#09659c",
              fontWeight: 600,
            }}
            onClick={() => handleAddNewNonLabor()}
          >
            + Add New
          </span>
        ) : (
          "No Data"
        )}
      </div>
    )}
  </>
  );
};
export default RenderLaborODC;
